import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, Text } from '@shootsta/common-react';

interface FadedTextInputProps {
  defaultValue: string;
  placeholder: string;
  onSwitchToListView: () => void;
}

function FadedTextInput({
  defaultValue,
  placeholder,
  onSwitchToListView
}: FadedTextInputProps) {
  const [needsFade, setNeedsFade] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [textValue, setTextValue] = useState(defaultValue);
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  const textRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if content overflows and needs fade effect
  const checkOverflow = useCallback(() => {
    if (textRef.current) {
      // Content overflows if scrollHeight exceeds clientHeight
      const hasOverflow =
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setNeedsFade(hasOverflow);
    }
  }, []);

  // Check overflow on mount and when value changes
  useEffect(() => {
    checkOverflow();
  }, [textValue, checkOverflow]);

  // Also check on resize
  useEffect(() => {
    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkOverflow]);

  // Update textValue when defaultValue changes (external updates)
  useEffect(() => {
    setTextValue(defaultValue);
  }, [defaultValue]);

  // Handle escape key and click outside to close overlay
  useEffect(() => {
    if (!isOverlayOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOverlayOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(e.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOverlayOpen(false);
      }
    };

    const handleScrollOutside = (e: Event) => {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(e.target as Node)
      ) {
        setIsOverlayOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScrollOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScrollOutside, true);
    };
  }, [isOverlayOpen]);

  const handleOpenOverlay = () => {
    if (isOverlayOpen) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width
      });
      setIsOverlayOpen(true);
    }
  };

  const handleSwitchToListView = () => {
    setIsOverlayOpen(false);
    onSwitchToListView();
  };

  return (
    <div
      ref={containerRef}
      className="relative cursor-pointer"
      onClick={needsFade ? handleOpenOverlay : undefined}
    >
      {/* Plain text preview with fade effect */}
      {textValue ? (
        <p
          ref={textRef}
          className={`min-h-[72px] text-sm text-slateBlueGray leading-6 m-0 line-clamp-3 ${
            needsFade
              ? '[mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)]'
              : ''
          }`}
        >
          {textValue}
        </p>
      ) : (
        <Text placeholder noMargin>
          {placeholder}
        </Text>
      )}

      {/* Popover - rendered via portal with fixed positioning to escape overflow-hidden */}
      {needsFade &&
        ReactDOM.createPortal(
          <div
            ref={overlayRef}
            className="fixed  bg-[#323A45] rounded-lg shadow-lg flex flex-col gap-2 py-2"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: popoverPosition.width,
              maxHeight: 288,
              opacity: isOverlayOpen ? 1 : 0,
              pointerEvents: isOverlayOpen ? 'auto' : 'none',
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            {/* Switch to list view link */}
            <div className="flex justify-start w-full px-3">
              <Link onClick={handleSwitchToListView}>Switch to List View</Link>
            </div>

            {/* Scrollable text content */}
            <div className="overflow-y-auto flex-1 pl-3 pr-2 min-h-0 [scrollbar-color:#C1C1C1_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#C1C1C1] [&::-webkit-scrollbar-thumb]:rounded-full">
              <Text
                darkTheme
                className="text-xs font-normal leading-5 tracking-normal"
              >
                {textValue || placeholder}
              </Text>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default FadedTextInput;
